import React from 'react'
import type { ColumnsType, ColumnType } from 'antd/es/table'

export interface ResizableHeaderCellProps
  extends Omit<
    React.ThHTMLAttributes<HTMLTableCellElement>,
    'onResize' | 'width'
  > {
  onResize?: (width: number) => void
  width?: number
}

export function ResizableTitle({
  onResize,
  width,
  children,
  style,
  ...restProps
}: ResizableHeaderCellProps) {
  // `restProps` carries AntD's sort wiring (`onClick`, `onKeyDown`, `tabIndex`,
  // `aria-sort`) injected via `onHeaderCell`. It MUST be spread onto the <th> or
  // the header looks sortable (cursor + arrows from the `-column-has-sorters`
  // class) but clicking does nothing.
  if (!onResize) {
    return (
      <th {...restProps} style={style}>
        {children}
      </th>
    )
  }
  return (
    <th {...restProps} style={{ ...style, position: 'relative' }}>
      {children}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: 'col-resize',
          zIndex: 1,
        }}
        // Stop the click from bubbling to the <th> sort handler so a resize
        // drag (or a click that lands on the 6px grip) never triggers a sort.
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => {
          const startX = e.clientX
          const startWidth = width ?? 100
          const onMouseMove = (ev: MouseEvent) => {
            onResize(Math.max(60, startWidth + ev.clientX - startX))
          }
          const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove)
            document.removeEventListener('mouseup', onMouseUp)
          }
          document.addEventListener('mousemove', onMouseMove)
          document.addEventListener('mouseup', onMouseUp)
          e.preventDefault()
          e.stopPropagation()
        }}
      />
    </th>
  )
}

// Applies stored widths + the resize handler to each column. The column array
// stays fully type-checked; only the non-standard `onHeaderCell` payload
// (`width`/`onResize`, which AntD's header-cell prop type doesn't model) is
// cast — not the whole `ColumnsType<T>`, so render/sorter/dataIndex drift is
// still caught by the compiler.
export function withResizableColumns<T>(
  columns: ColumnsType<T>,
  widths: Record<string, number>,
  onResize: (key: string) => (width: number) => void
): ColumnsType<T> {
  return columns.map((col) => {
    const key = col.key as string
    const w = widths[key]
    return {
      ...col,
      width: w,
      onHeaderCell: () =>
        ({
          width: w,
          onResize: onResize(key),
        }) as unknown as React.HTMLAttributes<HTMLElement>,
    } as ColumnType<T>
  })
}
